// src/utils/receiptGenerator.js
import { jsPDF } from 'jspdf'
import { format } from 'date-fns'

export class ReceiptGenerator {
  /**
   * Generate a PDF receipt for a sale
   * @param {Object} sale - Sale data
   * @param {Object} options - Options for receipt generation
   * @returns {Blob} PDF blob
   */
  static generateReceiptPDF(sale, options = {}) {
    const {
      companyName = 'Tezlaa Cafe',
      companyAddress = 'Your Business Address',
      companyPhone = 'Your Phone Number',
      companyEmail = 'contact@tezlaa.com'
    } = options

    // Create new PDF document
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    })

    // Set up fonts and colors
    const primaryColor = [255, 138, 0] // Orange
    const textColor = [33, 37, 41] // Dark gray
    const lightGray = [248, 249, 250]

    // Page dimensions
    const pageWidth = doc.internal.pageSize.width
    const margin = 20
    const contentWidth = pageWidth - (margin * 2)

    let yPos = margin

    // Header Section
    doc.setFillColor(...primaryColor)
    doc.rect(0, 0, pageWidth, 40, 'F')

    // Company name
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(24)
    doc.setFont('helvetica', 'bold')
    doc.text(companyName, margin, yPos + 15)

    // Receipt title
    doc.setFontSize(16)
    doc.text('RECEIPT', pageWidth - margin - 30, yPos + 15)

    yPos += 50

    // Company information
    doc.setTextColor(...textColor)
    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.text(`${companyAddress}`, margin, yPos)
    yPos += 5
    doc.text(`Phone: ${companyPhone} | Email: ${companyEmail}`, margin, yPos)
    yPos += 15

    // Order information section
    doc.setFillColor(...lightGray)
    doc.rect(margin, yPos, contentWidth, 35, 'F')

    doc.setTextColor(...textColor)
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    yPos += 10

    // Order details - Left column
    doc.text(`Order #: ${sale.orderNumber || 'N/A'}`, margin + 5, yPos)
    yPos += 7
    doc.text(`Date: ${this.formatDate(sale.orderDate)}`, margin + 5, yPos)
    yPos += 7
    doc.text(`Type: ${sale.orderTypeConfig?.label || 'N/A'}`, margin + 5, yPos)

    // Customer details - Right column
    yPos -= 14
    doc.text(`Customer: ${sale.customerName || 'Walk-in Customer'}`, margin + 100, yPos)
    yPos += 7
    if (sale.customerMobile) {
      doc.text(`Phone: ${sale.customerMobile}`, margin + 100, yPos)
    }
    yPos += 7
    doc.text(`Branch: ${sale.branchName || 'N/A'}`, margin + 100, yPos)

    yPos += 20

    // Items table header
    doc.setFillColor(...primaryColor)
    doc.rect(margin, yPos, contentWidth, 12, 'F')

    doc.setTextColor(255, 255, 255)
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.text('Item', margin + 5, yPos + 8)
    doc.text('Qty', margin + 100, yPos + 8)
    doc.text('Price', margin + 125, yPos + 8)
    doc.text('Total', margin + 150, yPos + 8)

    yPos += 12

    // Items
    doc.setTextColor(...textColor)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(10)

    const items = sale.items || []
    items.forEach((item, index) => {
      if (index % 2 === 0) {
        doc.setFillColor(...lightGray)
        doc.rect(margin, yPos, contentWidth, 8, 'F')
      }

      const itemName = item.name || item.productName || item.product_name || item.itemName || 
                      item.title || item.description || `Item #${index + 1}`
      const quantity = item.quantity || 1
      const price = this.formatCurrency(item.price || item.unitPrice || 0)
      const total = this.formatCurrency((item.price || item.unitPrice || 0) * quantity)

      doc.text(itemName, margin + 5, yPos + 6)
      doc.text(quantity.toString(), margin + 100, yPos + 6)
      doc.text(price, margin + 125, yPos + 6)
      doc.text(total, margin + 150, yPos + 6)

      yPos += 8
    })

    yPos += 10

    // Totals section
    doc.setFillColor(...lightGray)
    doc.rect(margin + 90, yPos, contentWidth - 90, 35, 'F')

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(10)

    // Subtotal
    const subtotal = sale.subtotal || 0
    const tax = sale.tax || 0
    const discount = sale.discount || 0
    const total = sale.total || 0

    doc.text('Subtotal:', margin + 95, yPos + 8)
    doc.text(this.formatCurrency(subtotal), margin + 150, yPos + 8)
    yPos += 6

    if (discount > 0) {
      doc.text('Discount:', margin + 95, yPos + 8)
      doc.text(`-${this.formatCurrency(discount)}`, margin + 150, yPos + 8)
      yPos += 6
    }

    if (tax > 0) {
      doc.text('Tax/Service Fee:', margin + 95, yPos + 8)
      doc.text(this.formatCurrency(tax), margin + 150, yPos + 8)
      yPos += 6
    }

    // Total
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(12)
    doc.text('TOTAL:', margin + 95, yPos + 8)
    doc.text(this.formatCurrency(total), margin + 150, yPos + 8)

    yPos += 20

    // Payment information
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(10)
    doc.text(`Payment Method: ${sale.paymentMethodConfig?.label || 'Cash'}`, margin, yPos)
    yPos += 5
    doc.text(`Payment Status: ${sale.paymentStatusConfig?.label || 'Paid'}`, margin, yPos)

    yPos += 20

    // Footer
    doc.setTextColor(128, 128, 128)
    doc.setFontSize(9)
    doc.text('Thank you for your business!', margin, yPos)
    doc.text(`Generated on: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, margin, yPos + 5)

    // Generate and return blob
    return doc.output('blob')
  }

  /**
   * Format date for display
   * @param {string|Date} date - Date to format
   * @returns {string} Formatted date
   */
  static formatDate(date) {
    if (!date) return 'N/A'
    
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date
      return format(dateObj, 'dd/MM/yyyy HH:mm')
    } catch {
      console.warn('Error formatting date:', date)
      return 'Invalid Date'
    }
  }

  /**
   * Format currency value
   * @param {number} amount - Amount to format
   * @returns {string} Formatted currency
   */
  static formatCurrency(amount) {
    if (typeof amount !== 'number' || isNaN(amount)) return 'Rs.0.00'
    return `Rs.${amount.toFixed(2)}`
  }

  /**
   * Download the PDF receipt
   * @param {Object} sale - Sale data
   * @param {string} filename - Optional filename
   * @param {Object} options - Options for receipt generation
   */
  static downloadReceipt(sale, filename = null, options = {}) {
    const blob = this.generateReceiptPDF(sale, options)
    
    // Create download link
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename || `receipt_${sale.orderNumber || Date.now()}.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }
}